import axios, { AxiosResponse } from "axios";
import { AuthResponse, Template, CreateTemplateData } from "../types";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api-postify.eymel.co/api";

console.log("API initialized with base URL:", API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor ile token ve logging
api.interceptors.request.use(
  (config) => {
    console.log("Making API request:", {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
    });

    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log("Token added to request");
    } else {
      console.log("No token found in localStorage");
    }

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async register(email: string, password: string): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await api.post(
      "/auth/register",
      {
        email,
        password,
      }
    );
    return response.data;
  },

  async login(email: string, password: string): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await api.post(
      "/auth/login",
      {
        email,
        password,
      }
    );
    return response.data;
  },

  async regenerateApiKey(): Promise<{ apiKey: string; message: string }> {
    const response = await api.post("/auth/regenerate-api-key");
    return response.data;
  },
};

export const templateService = {
  async getTemplates(): Promise<Template[]> {
    const response: AxiosResponse<Template[]> = await api.get("/templates");
    return response.data;
  },

  async getTemplate(id: string): Promise<Template> {
    console.log("templateService.getTemplate called with ID:", id);
    console.log("API base URL:", api.defaults.baseURL);
    console.log("Making request to:", `/templates/${id}`);

    try {
      const response: AxiosResponse<Template> = await api.get(
        `/templates/${id}`
      );
      console.log(
        "Template response received:",
        response.status,
        response.data
      );
      return response.data;
    } catch (error) {
      console.error("templateService.getTemplate error:", error);
      throw error;
    }
  },

  async createTemplate(
    data: CreateTemplateData
  ): Promise<{ message: string; template: Template }> {
    const response = await api.post("/templates", data);
    return response.data;
  },

  async updateTemplate(
    id: string,
    data: CreateTemplateData
  ): Promise<{ message: string; template: Template }> {
    const response = await api.put(`/templates/${id}`, data);
    return response.data;
  },

  async deleteTemplate(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/templates/${id}`);
    return response.data;
  },
};

export const fontService = {
  async getFonts(): Promise<{
    success: boolean;
    fonts: {
      system: string[];
      google: string[];
      categories: {
        "sans-serif": string[];
        serif: string[];
        monospace: string[];
      };
    };
    total: number;
  }> {
    const response = await api.get("/fonts");
    return response.data;
  },

  loadGoogleFont: (fontName: string) => api.post(`/fonts/google/${fontName}`),

  getFontPreviewUrl: (fontName: string, text?: string, size?: number) => {
    const params = new URLSearchParams();
    if (text) params.append("text", text);
    if (size) params.append("size", size.toString());

    const queryString = params.toString();
    return `${API_BASE_URL}/fonts/preview/${fontName}${
      queryString ? "?" + queryString : ""
    }`;
  },
};

export const renderService = {
  async renderTemplate(
    templateId: string,
    data: Record<string, string>
  ): Promise<any> {
    const response = await api.post(`/render/template`, { templateId, data });
    return response.data;
  },

  async renderTemplateBase64(
    templateId: string,
    data: Record<string, string>
  ): Promise<{
    image: string;
    imageUrl?: string;
    templateId: string;
    renderCount: number;
  }> {
    const response = await api.post(`/render/template/base64`, {
      templateId,
      data,
    });
    return response.data;
  },

  getPreviewUrl(templateId: string): string {
    return `${API_BASE_URL}/render/preview/${templateId}`;
  },
};

// Render History Service
export const renderHistoryService = {
  getTemplateRenderHistory: (
    templateId: string,
    page: number = 1,
    limit: number = 20
  ) =>
    api.post(`/render/template/history?page=${page}&limit=${limit}`, {
      templateId,
    }),
};

export default api;
